// ═══════════════════════════════════════════════════════════════════════
// agenticAITab.jsx — Agentic AI design & configuration surface
// ═══════════════════════════════════════════════════════════════════════
// Interactive design tool covering:
//   · 27 agents configurable per-agent: autonomy level, enabled, pilot scope, notes
//   · 6 autonomy levels visualized as a spectrum
//   · 12 human-control gates (immutable bright lines)
//   · 3 governance bodies (decision rights, cadence, members)
//   · 8 KPIs (4 agent + 4 program) with targets and watch-fors
//   · 5 implementation phases on a roadmap
//   · 4-level kill-switch model
// State persists via reducer (SET_AGENT_LEVEL, TOGGLE_AGENT_ENABLED, SET_AGENT_NOTE).
// ═══════════════════════════════════════════════════════════════════════

import React, { useState, useMemo } from "react";
import {
  AUTONOMY_LEVELS, AGENT_ZONES, AGENTS, HUMAN_GATES,
  GOVERNANCE_BODIES, AGENTIC_KPIS, PHASES, KILL_SWITCH,
} from "./agenticAIData";
import { T, FONTS } from "./theme";
import { getInterfacesForAgent, getInterfacesForGate } from "./crossRefs";
import { EntityRefList } from "./navigation";

const SECTIONS = [
  { id: "agents",     label: "🤖 Agents (27)",        color: T.cyan },
  { id: "autonomy",   label: "📊 Autonomy spectrum",   color: T.green },
  { id: "gates",      label: "🛡️ Human gates (12)",   color: T.pink },
  { id: "governance", label: "⚖️ Governance bodies",   color: T.purple },
  { id: "kpis",       label: "📈 KPIs (8)",            color: T.gold },
  { id: "killswitch", label: "🛑 Kill switch",        color: T.red },
  { id: "roadmap",    label: "🗓️ 24-mo roadmap",      color: T.amber },
];

export default function AgenticAITab({ state, dispatch }) {
  const [section, setSection] = useState("agents");
  const [zoneFilter, setZoneFilter] = useState("ALL");
  const [expandedAgent, setExpandedAgent] = useState(null);

  const agentConfig = state.agentConfig || {};

  // Effective level per agent — default to AGENTS[i].defaultLevel
  const effectiveLevel = (agent) =>
    agentConfig[agent.id]?.level || agent.defaultLevel;
  const isEnabled = (agent) =>
    agentConfig[agent.id]?.enabled !== false;  // enabled by default

  // Aggregate stats
  const stats = useMemo(() => {
    const byLevel = {};
    const byZone  = {};
    let enabled = 0, disabled = 0;
    for (const a of AGENTS) {
      const lvl = effectiveLevel(a);
      byLevel[lvl] = (byLevel[lvl] || 0) + 1;
      byZone[a.zone] = (byZone[a.zone] || 0) + 1;
      if (isEnabled(a)) enabled++; else disabled++;
    }
    return { byLevel, byZone, enabled, disabled };
  }, [agentConfig]);

  const filteredAgents = zoneFilter === "ALL"
    ? AGENTS
    : AGENTS.filter(a => a.zone === zoneFilter);

  return (
    <div style={{ padding: "20px 24px", maxHeight: "calc(100vh - 108px)", overflowY: "auto" }}>
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontFamily: FONTS.header, fontSize: 16, fontWeight: 700, color: T.cyan, marginBottom: 4 }}>
          🤖 Agentic AI for CLM · Design Surface
        </div>
        <div style={{ fontSize: 11, color: T.text2 }}>
          27 agents · 6 autonomy levels · 12 inviolable human gates · 3 governance bodies · 8 KPIs · 24-month roadmap
        </div>
        <div style={{ fontSize: 9.5, color: T.amber, marginTop: 6, fontStyle: "italic" }}>
          "Agents do the routine. Humans own the consequential. Audit governs both."
        </div>
      </div>

      {/* Aggregate KPI strip */}
      <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
        {[
          { l: "Agents",   v: AGENTS.length, c: T.cyan },
          { l: "Enabled",  v: stats.enabled, c: T.green },
          { l: "Disabled", v: stats.disabled, c: T.red },
          { l: "L0–L1",    v: (stats.byLevel.L0 || 0) + (stats.byLevel.L1 || 0), c: AUTONOMY_LEVELS[1].color },
          { l: "L2",       v: stats.byLevel.L2 || 0, c: AUTONOMY_LEVELS[2].color },
          { l: "L3",       v: stats.byLevel.L3 || 0, c: AUTONOMY_LEVELS[3].color },
          { l: "L4+",      v: (stats.byLevel.L4 || 0) + (stats.byLevel.L5 || 0), c: AUTONOMY_LEVELS[4].color },
          { l: "Gates",    v: HUMAN_GATES.length, c: T.pink },
        ].map(k => (
          <div key={k.l} style={{
            background: T.bg2, border: `1px solid ${k.c}30`, borderRadius: 6,
            padding: "8px 14px", minWidth: 70, textAlign: "center",
          }}>
            <div style={{ fontFamily: FONTS.header, fontSize: 16, fontWeight: 700, color: k.c }}>{k.v}</div>
            <div style={{ fontSize: 7.5, color: T.text2, letterSpacing: 1, textTransform: "uppercase" }}>{k.l}</div>
          </div>
        ))}
      </div>

      {/* Section nav */}
      <div style={{ display: "flex", gap: 6, marginBottom: 20, flexWrap: "wrap" }}>
        {SECTIONS.map(s => (
          <button key={s.id} onClick={() => setSection(s.id)}
            style={{
              padding: "6px 14px", fontSize: 10, fontFamily: FONTS.mono,
              background: section === s.id ? `${s.color}15` : T.bg3,
              border: `1px solid ${section === s.id ? s.color : T.border}`,
              color: section === s.id ? s.color : T.text2,
              borderRadius: 4, cursor: "pointer",
              fontWeight: section === s.id ? 700 : 400,
              letterSpacing: 1,
            }}>
            {s.label}
          </button>
        ))}
      </div>

      {/* ─── AGENTS SECTION ─── */}
      {section === "agents" && (
        <div>
          <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap" }}>
            <button onClick={() => setZoneFilter("ALL")}
              style={zoneBtn(zoneFilter === "ALL", T.cyan)}>
              All zones ({AGENTS.length})
            </button>
            {AGENT_ZONES.map(z => {
              const count = AGENTS.filter(a => a.zone === z.id).length;
              return (
                <button key={z.id} onClick={() => setZoneFilter(z.id)}
                  style={zoneBtn(zoneFilter === z.id, z.color)}>
                  {z.icon} {z.label} ({count})
                </button>
              );
            })}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(380px, 1fr))", gap: 12 }}>
            {filteredAgents.map(agent => {
              const zone   = AGENT_ZONES.find(z => z.id === agent.zone);
              const level  = AUTONOMY_LEVELS.find(l => l.id === effectiveLevel(agent));
              const cfg    = agentConfig[agent.id] || {};
              const enabled = isEnabled(agent);
              const isOpen = expandedAgent === agent.id;

              return (
                <div key={agent.id} style={{
                  background: T.bg2,
                  border: `1px solid ${enabled ? zone.color + "40" : T.border2}`,
                  borderLeft: `4px solid ${enabled ? zone.color : T.text3}`,
                  borderRadius: 8, padding: "12px 14px",
                  opacity: enabled ? 1 : 0.55, transition: "opacity .2s",
                }}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                    <div style={{ fontSize: 24 }}>{zone.icon}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 4 }}>
                        <span style={{
                          fontFamily: FONTS.header, fontSize: 9,
                          color: zone.color, fontWeight: 700,
                        }}>{agent.id}</span>
                        <span style={{ fontSize: 12, fontWeight: 700, color: T.text }}>{agent.name}</span>
                        {agent.pilot && <span style={pillStyle(T.amber)}>🚀 pilot</span>}
                      </div>
                      <div style={{ fontSize: 9.5, color: T.text2, lineHeight: 1.45, marginBottom: 6 }}>
                        {agent.role}
                      </div>
                      <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                        <span style={{
                          ...pillStyle(level.color),
                          fontWeight: 700, fontSize: 9,
                        }}>
                          {level.id} · {level.name}
                        </span>
                        <span style={pillStyle(zone.color)}>{zone.label}</span>
                        <span style={{ fontSize: 8, color: T.text3 }}>
                          {level.humanPct}% human / {level.agentPct}% agent
                        </span>
                      </div>
                    </div>
                    <button onClick={() => setExpandedAgent(isOpen ? null : agent.id)}
                      style={{
                        background: "none", border: "none", color: zone.color,
                        cursor: "pointer", fontSize: 14,
                        transform: isOpen ? "rotate(90deg)" : "none",
                        transition: "transform .15s",
                      }}>▸</button>
                  </div>

                  {isOpen && (
                    <div style={{
                      marginTop: 12, paddingTop: 12,
                      borderTop: `1px solid ${T.border2}`,
                    }}>
                      {/* Autonomy level selector */}
                      <div style={{ marginBottom: 14 }}>
                        <div style={{ fontSize: 8.5, color: T.text2, letterSpacing: 1, textTransform: "uppercase", marginBottom: 6 }}>
                          ⚙️ Autonomy level
                        </div>
                        <div style={{ display: "flex", gap: 3 }}>
                          {AUTONOMY_LEVELS.map(l => {
                            const isSelected = l.id === effectiveLevel(agent);
                            const isDefault  = l.id === agent.defaultLevel;
                            return (
                              <button key={l.id}
                                onClick={() => dispatch({ type: "SET_AGENT_LEVEL", agentId: agent.id, level: l.id })}
                                style={{
                                  flex: 1, padding: "6px 4px", borderRadius: 3,
                                  background: isSelected ? `${l.color}25` : T.bg3,
                                  border: `1px solid ${isSelected ? l.color : T.border}`,
                                  color: isSelected ? l.color : T.text2,
                                  fontSize: 9, fontFamily: FONTS.mono,
                                  fontWeight: isSelected ? 700 : 400,
                                  cursor: "pointer",
                                  position: "relative",
                                }}
                                title={l.desc}>
                                {l.id}
                                {isDefault && <span style={{
                                  position: "absolute", top: -4, right: -2,
                                  background: T.amber, color: "#000",
                                  fontSize: 5, padding: "1px 2px",
                                  borderRadius: 2, fontWeight: 700,
                                }}>def</span>}
                              </button>
                            );
                          })}
                        </div>
                        <div style={{ fontSize: 8.5, color: T.text3, marginTop: 6, fontStyle: "italic" }}>
                          {AUTONOMY_LEVELS.find(l => l.id === effectiveLevel(agent))?.desc}
                        </div>
                      </div>

                      {/* Tools */}
                      <div style={{ marginBottom: 10 }}>
                        <div style={{ fontSize: 8.5, color: T.text2, letterSpacing: 1, textTransform: "uppercase", marginBottom: 4 }}>
                          🔧 Tools allowed
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                          {agent.tools.map(t => (
                            <span key={t} style={{
                              fontSize: 8, fontFamily: FONTS.mono,
                              padding: "1px 6px", borderRadius: 3,
                              background: `${zone.color}15`, color: zone.color,
                            }}>{t}</span>
                          ))}
                        </div>
                      </div>

                      {/* Escalation rules */}
                      <div style={{ marginBottom: 10 }}>
                        <div style={{ fontSize: 8.5, color: T.pink, letterSpacing: 1, textTransform: "uppercase", marginBottom: 4 }}>
                          ⬆ Escalates to human
                        </div>
                        <div style={{ fontSize: 9.5, color: T.text2, fontStyle: "italic" }}>
                          {agent.escalations}
                        </div>
                      </div>

                      {/* KPI */}
                      {agent.kpi && (
                        <div style={{ marginBottom: 10 }}>
                          <div style={{ fontSize: 8.5, color: T.green, letterSpacing: 1, textTransform: "uppercase", marginBottom: 4 }}>
                            📈 Target KPI
                          </div>
                          <div style={{ fontSize: 9.5, color: T.green }}>{agent.kpi}</div>
                        </div>
                      )}

                      {/* NEW: Interfaces this agent operates on */}
                      <div style={{ marginBottom: 10 }}>
                        <EntityRefList
                          kind="interface"
                          ids={getInterfacesForAgent(agent.id)}
                          label="🔗 Interfaces operated on"
                          emptyText="(no interface coverage yet — agent is informational or out-of-scope)"
                        />
                      </div>

                      {/* Enable/disable + notes */}
                      <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 10 }}>
                        <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 10, cursor: "pointer" }}>
                          <input type="checkbox" checked={enabled}
                            onChange={() => dispatch({ type: "TOGGLE_AGENT_ENABLED", agentId: agent.id })} />
                          <span style={{ color: enabled ? T.green : T.text3 }}>{enabled ? "ENABLED" : "DISABLED"}</span>
                        </label>
                      </div>

                      <textarea
                        value={cfg.note || ""}
                        onChange={e => dispatch({ type: "SET_AGENT_NOTE", agentId: agent.id, note: e.target.value })}
                        placeholder="Configuration notes: tuning, blockers, owner, eval results..."
                        style={{
                          width: "100%", minHeight: 40, marginTop: 8,
                          padding: "6px 8px", background: T.bg3,
                          border: `1px solid ${T.border}`, color: T.text,
                          fontSize: 9, borderRadius: 4, outline: "none", resize: "vertical",
                        }} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ─── AUTONOMY SPECTRUM ─── */}
      {section === "autonomy" && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 10 }}>
          {AUTONOMY_LEVELS.map(l => (
            <div key={l.id} style={{
              background: T.bg2,
              border: `1px solid ${l.color}30`, borderTop: `3px solid ${l.color}`,
              borderRadius: 6, padding: "14px 12px",
            }}>
              <div style={{ fontFamily: FONTS.header, fontSize: 28, fontWeight: 700, color: l.color, marginBottom: 4 }}>
                {l.id}
              </div>
              <div style={{ fontSize: 11, fontWeight: 600, color: T.text, letterSpacing: 1, textTransform: "uppercase", marginBottom: 8 }}>
                {l.name}
              </div>
              <div style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 8, color: T.pink, fontWeight: 600 }}>HUMAN</div>
                <div style={{ fontSize: 16, color: T.pink, fontWeight: 700 }}>{l.humanPct}%</div>
                <div style={{ fontSize: 8, color: T.green, fontWeight: 600, marginTop: 4 }}>AGENT</div>
                <div style={{ fontSize: 16, color: T.green, fontWeight: 700 }}>{l.agentPct}%</div>
              </div>
              <div style={{ fontSize: 9.5, color: T.text2, lineHeight: 1.5, marginBottom: 10 }}>
                {l.desc}
              </div>
              <div style={{ fontSize: 8, color: T.text3, textTransform: "uppercase", letterSpacing: 1 }}>Example</div>
              <div style={{ fontSize: 9, color: T.text2, fontStyle: "italic", marginTop: 4 }}>
                {l.example}
              </div>
              <div style={{ fontSize: 9, color: l.color, marginTop: 10, fontWeight: 600 }}>
                {stats.byLevel[l.id] || 0} agents currently at this level
              </div>
            </div>
          ))}
          <div style={{
            gridColumn: "1 / -1",
            background: T.bg2, border: `1px solid ${T.pink}30`, borderRadius: 6,
            padding: "12px 16px", marginTop: 10,
          }}>
            <div style={{ fontSize: 9, color: T.text2 }}>
              <strong style={{ color: T.pink }}>← LESS AUTONOMOUS · MORE HUMAN CONTROL</strong>
              <span style={{ float: "right", color: T.green }}><strong>MORE AUTONOMOUS · LESS HUMAN CONTROL →</strong></span>
            </div>
            <div style={{ fontSize: 9, color: T.amber, marginTop: 6, fontStyle: "italic" }}>
              L1 is the default for new agents · L3 requires 6+ months track record · L4/L5 require Ethics & Safety Committee approval.
            </div>
          </div>
        </div>
      )}

      {/* ─── 12 HUMAN GATES ─── */}
      {section === "gates" && (
        <div>
          <div style={{
            padding: "10px 14px", marginBottom: 14,
            background: `${T.pink}10`, border: `1px solid ${T.pink}30`, borderRadius: 6,
            fontSize: 10, color: T.pink,
          }}>
            ⚠️ <strong>Bright lines.</strong> No agent autonomy level — including L5 — overrides these.
            Hard-coded in the policy engine. Any attempt by an agent to cross a gate must trigger immediate human escalation.
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: 10 }}>
            {HUMAN_GATES.map(g => {
              const protectedIfaces = getInterfacesForGate(g.num);
              return (
                <div key={g.num} style={{
                  background: T.bg2, border: `1px solid ${T.pink}30`,
                  borderLeft: `3px solid ${T.pink}`, borderRadius: 6, padding: "12px 14px",
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: T.text, marginBottom: 6 }}>
                      {g.name}
                    </div>
                    <div style={{
                      fontFamily: FONTS.header, fontSize: 18, fontWeight: 700, color: T.pink,
                      opacity: 0.5,
                    }}>
                      {String(g.num).padStart(2, "0")}
                    </div>
                  </div>
                  <div style={{ fontSize: 9.5, color: T.text2, lineHeight: 1.5, marginBottom: 8 }}>
                    {g.detail}
                  </div>
                  {protectedIfaces.length > 0 && (
                    <EntityRefList kind="interface" ids={protectedIfaces} label="Protects" />
                  )}
                </div>
              );
            })}
          </div>

          <div style={{
            padding: "10px 14px", marginTop: 14,
            background: T.bg2, border: `1px solid ${T.amber}30`, borderRadius: 6,
            fontSize: 9, color: T.amber, fontStyle: "italic",
          }}>
            12 gates · hard-coded in policy · no agent autonomy level overrides these · ever.
          </div>
        </div>
      )}

      {/* ─── GOVERNANCE BODIES ─── */}
      {section === "governance" && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: 14 }}>
          {GOVERNANCE_BODIES.map(b => (
            <div key={b.id} style={{
              background: T.bg2, border: `1px solid ${b.color}40`,
              borderTop: `4px solid ${b.color}`, borderRadius: 8, padding: "14px 16px",
            }}>
              <div style={{ fontFamily: FONTS.header, fontSize: 12, fontWeight: 700, color: b.color, letterSpacing: 1, marginBottom: 4 }}>
                {b.name}
              </div>
              <div style={{ fontSize: 9, color: T.text3, fontStyle: "italic", marginBottom: 12 }}>
                {b.scope}
              </div>

              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 8, color: b.color, letterSpacing: 1, textTransform: "uppercase", marginBottom: 6 }}>
                  ⚖️ Decides
                </div>
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: 9.5, color: T.text2, lineHeight: 1.5 }}>
                  {b.decides.map((d, i) => <li key={i}>{d}</li>)}
                </ul>
              </div>

              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 8, color: b.color, letterSpacing: 1, textTransform: "uppercase", marginBottom: 4 }}>
                  ⏰ Cadence
                </div>
                <div style={{ fontSize: 9.5, color: T.text2 }}>{b.cadence}</div>
              </div>

              <div>
                <div style={{ fontSize: 8, color: b.color, letterSpacing: 1, textTransform: "uppercase", marginBottom: 4 }}>
                  👥 Members
                </div>
                <div style={{ fontSize: 9, color: T.text2, fontStyle: "italic" }}>{b.members}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ─── KPIs ─── */}
      {section === "kpis" && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 12 }}>
          {AGENTIC_KPIS.map(k => (
            <div key={k.name} style={{
              background: T.bg2, border: `1px solid ${k.color}30`,
              borderTop: `3px solid ${k.color}`, borderRadius: 6, padding: "12px 14px",
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                <span style={{
                  fontFamily: FONTS.header, fontSize: 11,
                  color: k.color, fontWeight: 700, letterSpacing: 1,
                }}>{k.name}</span>
                <span style={{
                  fontSize: 7, padding: "2px 6px", borderRadius: 8,
                  background: `${k.color}20`, color: k.color, letterSpacing: 1,
                  textTransform: "uppercase",
                }}>{k.type} KPI</span>
              </div>
              <div style={{ fontSize: 10, color: T.text, marginBottom: 8 }}>{k.defn}</div>
              <div style={{ marginBottom: 6 }}>
                <span style={{ fontSize: 8, color: T.green, textTransform: "uppercase" }}>🎯 Target: </span>
                <span style={{ fontSize: 9, color: T.text2 }}>{k.target}</span>
              </div>
              <div>
                <span style={{ fontSize: 8, color: T.amber, textTransform: "uppercase" }}>👁 Watch: </span>
                <span style={{ fontSize: 9, color: T.text2, fontStyle: "italic" }}>{k.watch}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ─── KILL SWITCH ─── */}
      {section === "killswitch" && (
        <div>
          <div style={{
            padding: "10px 14px", marginBottom: 14,
            background: `${T.red}10`, border: `1px solid ${T.red}30`, borderRadius: 6,
            fontSize: 10, color: T.red,
          }}>
            🛑 <strong>Always one button away from off — always.</strong> If you can't stop an agent in one click,
            you should not have deployed the agent. Drilled monthly in production.
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 12 }}>
            {KILL_SWITCH.map(k => (
              <div key={k.level} style={{
                background: T.bg2, border: `1px solid ${k.color}40`,
                borderTop: `3px solid ${k.color}`, borderRadius: 6, padding: "14px 16px",
                textAlign: "center",
              }}>
                <div style={{
                  fontFamily: FONTS.header, fontSize: 18, fontWeight: 700,
                  color: k.color, letterSpacing: 2, marginBottom: 12,
                }}>{k.level}</div>
                {[
                  { l: "WHEN", v: k.when },
                  { l: "WHO", v: k.who },
                  { l: "TIME", v: k.time },
                  { l: "REVERSE", v: k.reverse },
                ].map(row => (
                  <div key={row.l} style={{
                    paddingTop: 6, marginTop: 6,
                    borderTop: `1px solid ${k.color}20`,
                  }}>
                    <div style={{ fontSize: 7, color: k.color, letterSpacing: 1 }}>{row.l}</div>
                    <div style={{ fontSize: 9.5, color: T.text2, marginTop: 2 }}>{row.v}</div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ─── ROADMAP ─── */}
      {section === "roadmap" && (
        <div>
          <div style={{
            padding: "10px 14px", marginBottom: 14,
            background: T.bg2, border: `1px solid ${T.amber}30`, borderRadius: 6,
            fontSize: 10, color: T.amber,
          }}>
            🗓️ <strong>24 months · 5 phases · evidence at every gate.</strong>
            No agent enters production until its phase gate passes. No phase begins until prior evidence is reviewed.
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10 }}>
            {PHASES.map((p, i) => (
              <div key={p.id} style={{
                background: T.bg2, border: `1px solid ${p.color}40`,
                borderTop: `3px solid ${p.color}`, borderRadius: 6, padding: "12px 14px",
                position: "relative",
              }}>
                <div style={{ fontFamily: FONTS.header, fontSize: 10, color: p.color, letterSpacing: 1, fontWeight: 700 }}>
                  {p.id}
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginTop: 4 }}>
                  {p.name}
                </div>
                <div style={{ fontSize: 11, color: p.color, fontFamily: FONTS.mono, marginTop: 2 }}>
                  {p.months}
                </div>
                <div style={{ borderTop: `1px solid ${p.color}30`, margin: "10px 0 8px" }} />
                <div style={{ fontSize: 7.5, color: p.color, letterSpacing: 1, textTransform: "uppercase", marginBottom: 4 }}>Scope</div>
                <ul style={{ margin: 0, paddingLeft: 14, fontSize: 9, color: T.text2, lineHeight: 1.5 }}>
                  {p.scope.map((s, j) => <li key={j}>{s}</li>)}
                </ul>
                <div style={{ borderTop: `1px solid ${p.color}30`, margin: "10px 0 6px" }} />
                <div style={{ fontSize: 7.5, color: T.pink, letterSpacing: 1, textTransform: "uppercase", marginBottom: 4 }}>Phase gate</div>
                <div style={{ fontSize: 9, color: T.pink, fontStyle: "italic" }}>{p.gate}</div>
                {i < PHASES.length - 1 && (
                  <div style={{
                    position: "absolute", right: -8, top: "50%",
                    transform: "translateY(-50%)",
                    color: p.color, fontSize: 18, zIndex: 5,
                  }}>▸</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const zoneBtn = (active, color) => ({
  padding: "5px 12px", fontSize: 10, borderRadius: 4, cursor: "pointer",
  background: active ? `${color}15` : "#081525",
  border: `1px solid ${active ? color : "rgba(0,229,255,.1)"}`,
  color: active ? color : "#6a8fa8",
  fontFamily: FONTS.mono,
  fontWeight: active ? 700 : 400,
});
const pillStyle = (c) => ({
  fontSize: 8, padding: "1px 6px", borderRadius: 8,
  background: `${c}15`, color: c, border: `1px solid ${c}40`,
  fontFamily: FONTS.mono,
});
